import {
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import { FeaturePopup, TargetAudienceConfig } from '../models/FeaturePopup';
import { UserProfile } from '../models/FirestoreModels';
import { isMemberUnpaidOrExpired } from './firebaseUtils';

const POPUPS_COLLECTION = 'feature_popups';

let memoryStore: Record<string, string> = {};

function getStorage(): { getItem(k: string): string | null; setItem(k: string, v: string): void; removeItem(k: string): void; clear(): void } {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem('__acs_test__', '1');
      window.localStorage.removeItem('__acs_test__');
      return window.localStorage;
    }
  } catch {
    // Fallback if localStorage access is denied or throws SecurityError
  }

  try {
    if (typeof globalThis !== 'undefined' && (globalThis as any).localStorage) {
      (globalThis as any).localStorage.setItem('__acs_test__', '1');
      (globalThis as any).localStorage.removeItem('__acs_test__');
      return (globalThis as any).localStorage;
    }
  } catch {
    // Fallback
  }

  return {
    getItem: (k: string) => memoryStore[k] ?? null,
    setItem: (k: string, v: string) => { memoryStore[k] = String(v); },
    removeItem: (k: string) => { delete memoryStore[k]; },
    clear: () => { memoryStore = {}; },
  };
}

export function clearFeaturePopupMemoryStore(): void {
  memoryStore = {};
  try {
    getStorage().clear();
  } catch {
    // Ignore
  }
}

export function getOrCreateAnonymousClientId(): string {
  const storage = getStorage();
  if (!storage) return 'anon-default';
  try {
    const key = 'acs_client_id';
    let id = storage.getItem(key);
    if (!id) {
      id = 'anon_' + Math.random().toString(36).substring(2, 11) + Date.now().toString(36);
      storage.setItem(key, id);
    }
    return id;
  } catch {
    return 'anon-fallback';
  }
}

/**
 * Deterministically maps an ID to 'A' or 'B' for A/B marketing splits.
 */
export function getABBucket(id: string): 'A' | 'B' {
  if (!id) return 'A';
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = (hash << 5) - hash + id.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash) % 2 === 0 ? 'A' : 'B';
}

function parseIsoString(val: any): string {
  if (!val) return '';
  if (typeof val === 'string') return val;
  if (val instanceof Date) return val.toISOString();
  if (typeof val.toDate === 'function') return val.toDate().toISOString();
  if (typeof val.seconds === 'number') return new Date(val.seconds * 1000).toISOString();
  return '';
}

export function isUserMatchingAudience(
  targetAudience: TargetAudienceConfig,
  user: any,
  profile: UserProfile | null
): boolean {
  if (!targetAudience || targetAudience.type === 'all') {
    return true;
  }

  // Target specific members
  if (targetAudience.type === 'specific_members') {
    const memberIds = targetAudience.memberIds || [];
    if (!user?.uid) return false;
    return memberIds.includes(user.uid);
  }

  // Demographic & A/B Segment targeting
  if (targetAudience.type === 'demographic') {
    // 1. Membership status filter
    const status = targetAudience.membershipStatus || 'all';
    if (status !== 'all') {
      const isExpiredOrUnpaid = isMemberUnpaidOrExpired(profile);
      const isPaid = !isExpiredOrUnpaid && (profile?.isPaid === true);

      if (status === 'paid' && !isPaid) {
        return false;
      }
      if (status === 'unpaid' && profile?.isPaid === true) {
        return false;
      }
      if (status === 'expired' && !isExpiredOrUnpaid) {
        return false;
      }
    }

    // 2. A/B marketing bucket
    const abGroup = targetAudience.abGroup || 'all';
    if (abGroup !== 'all') {
      const identifier = user?.uid || getOrCreateAnonymousClientId();
      const userBucket = getABBucket(identifier);
      if (userBucket !== abGroup) {
        return false;
      }
    }

    return true;
  }

  return true;
}

export function isPopupDismissedOrSnoozed(popupId: string, popupVersion = 1): boolean {
  const storage = getStorage();
  if (!storage) return false;
  try {
    // Check permanent dismissal
    const dismissedKey = `acs_feature_popup_dismissed_${popupId}`;
    const dismissedRaw = storage.getItem(dismissedKey);
    if (dismissedRaw) {
      try {
        const parsed = JSON.parse(dismissedRaw);
        if (parsed && typeof parsed.version === 'number') {
          if (parsed.version >= popupVersion) {
            return true;
          }
        } else {
          return true;
        }
      } catch {
        return true;
      }
    }

    // Check temporary snooze ("Remind me later")
    const snoozedKey = `acs_feature_popup_snoozed_${popupId}`;
    const snoozedUntil = storage.getItem(snoozedKey);
    if (snoozedUntil) {
      const snoozeExpiry = parseInt(snoozedUntil, 10);
      if (!isNaN(snoozeExpiry) && Date.now() < snoozeExpiry) {
        return true;
      }
    }
  } catch (e) {
    console.warn('[featurePopupService] Error checking dismissal state', e);
  }
  return false;
}

export function dismissPopup(popupId: string, version = 1): void {
  const storage = getStorage();
  if (!storage) return;
  try {
    const dismissedKey = `acs_feature_popup_dismissed_${popupId}`;
    storage.setItem(dismissedKey, JSON.stringify({ version, dismissedAt: Date.now() }));
    // Clear any active snooze since it is now permanently dismissed
    storage.removeItem(`acs_feature_popup_snoozed_${popupId}`);
  } catch (e) {
    console.warn('[featurePopupService] Failed to record dismissal', e);
  }
}

export function snoozePopup(popupId: string, hours = 24): void {
  const storage = getStorage();
  if (!storage) return;
  try {
    const snoozedKey = `acs_feature_popup_snoozed_${popupId}`;
    const snoozeUntil = Date.now() + hours * 60 * 60 * 1000;
    storage.setItem(snoozedKey, snoozeUntil.toString());
  } catch (e) {
    console.warn('[featurePopupService] Failed to record snooze', e);
  }
}

export function resetPopupDismissal(popupId: string): void {
  const storage = getStorage();
  if (!storage) return;
  try {
    storage.removeItem(`acs_feature_popup_dismissed_${popupId}`);
    storage.removeItem(`acs_feature_popup_snoozed_${popupId}`);
  } catch (e) {
    console.warn('[featurePopupService] Failed to reset dismissal', e);
  }
}

export function evaluateEligiblePopup(
  popups: FeaturePopup[],
  user: any,
  profile: UserProfile | null,
  now: Date = new Date()
): FeaturePopup | null {
  const currentTime = now.getTime();

  for (const popup of popups) {
    if (!popup.isEnabled) continue;

    // Check Start Date
    if (popup.startDate) {
      const startTime = new Date(popup.startDate).getTime();
      if (!isNaN(startTime) && currentTime < startTime) {
        continue; // Not yet started
      }
    }

    // Check End Date (Expiration)
    if (popup.endDate) {
      const endTime = new Date(popup.endDate).getTime();
      if (!isNaN(endTime) && currentTime > endTime) {
        continue; // Expired
      }
    }

    // Check dismissal and snooze
    if (isPopupDismissedOrSnoozed(popup.id, popup.version || 1)) {
      continue;
    }

    // Check demographic & audience match
    if (!isUserMatchingAudience(popup.targetAudience, user, profile)) {
      continue;
    }

    // Found eligible popup!
    return popup;
  }

  return null;
}

export const featurePopupService = {
  async getAllPopups(): Promise<FeaturePopup[]> {
    try {
      const colRef = collection(db, POPUPS_COLLECTION);
      const snap = await getDocs(colRef);
      const popups = snap.docs.map((docSnap) => {
        const d = docSnap.data();
        return {
          id: docSnap.id,
          title: d.title || 'Untitled Promo',
          imageUrl: d.imageUrl || '',
          actionType: d.actionType || 'none',
          actionUrl: d.actionUrl || '',
          eventId: d.eventId || '',
          actionButtonText: d.actionButtonText || 'Get More Information',
          startDate: parseIsoString(d.startDate) || new Date().toISOString(),
          endDate: parseIsoString(d.endDate) || '',
          isEnabled: d.isEnabled !== false,
          targetAudience: d.targetAudience || { type: 'all' },
          version: typeof d.version === 'number' ? d.version : 1,
          createdAt: parseIsoString(d.createdAt),
          updatedAt: parseIsoString(d.updatedAt),
          createdBy: d.createdBy || '',
        } as FeaturePopup;
      });
      return popups.sort((a, b) => {
        const tA = a.createdAt ? new Date(a.createdAt).getTime() : (a.startDate ? new Date(a.startDate).getTime() : 0);
        const tB = b.createdAt ? new Date(b.createdAt).getTime() : (b.startDate ? new Date(b.startDate).getTime() : 0);
        return tB - tA;
      });
    } catch (e) {
      console.error('[featurePopupService] Failed to fetch popups:', e);
      return [];
    }
  },

  async getPopupById(id: string): Promise<FeaturePopup | null> {
    try {
      const docRef = doc(db, POPUPS_COLLECTION, id);
      const snap = await getDoc(docRef);
      if (!snap.exists()) return null;
      const d = snap.data();
      return {
        id: snap.id,
        title: d.title || 'Untitled Promo',
        imageUrl: d.imageUrl || '',
        actionType: d.actionType || 'none',
        actionUrl: d.actionUrl || '',
        eventId: d.eventId || '',
        actionButtonText: d.actionButtonText || 'Get More Information',
        startDate: parseIsoString(d.startDate) || new Date().toISOString(),
        endDate: parseIsoString(d.endDate) || '',
        isEnabled: d.isEnabled !== false,
        targetAudience: d.targetAudience || { type: 'all' },
        version: typeof d.version === 'number' ? d.version : 1,
        createdAt: parseIsoString(d.createdAt),
        updatedAt: parseIsoString(d.updatedAt),
        createdBy: d.createdBy || '',
      } as FeaturePopup;
    } catch (e) {
      console.error('[featurePopupService] Failed to get popup by id:', e);
      return null;
    }
  },

  async savePopup(popup: Partial<FeaturePopup>, userUid?: string): Promise<string> {
    const colRef = collection(db, POPUPS_COLLECTION);
    const id = popup.id || doc(colRef).id;
    const docRef = doc(db, POPUPS_COLLECTION, id);

    const dataToSave: any = {
      title: popup.title || 'Feature Announcement',
      imageUrl: popup.imageUrl || '',
      actionType: popup.actionType || 'none',
      actionUrl: popup.actionUrl || '',
      eventId: popup.eventId || '',
      actionButtonText: popup.actionButtonText || 'Get More Information',
      startDate: popup.startDate || new Date().toISOString(),
      endDate: popup.endDate || '',
      isEnabled: popup.isEnabled !== false,
      targetAudience: popup.targetAudience || { type: 'all' },
      version: popup.version || 1,
      updatedAt: serverTimestamp(),
    };

    if (!popup.id) {
      dataToSave.createdAt = serverTimestamp();
      if (userUid) {
        dataToSave.createdBy = userUid;
      }
    }

    await setDoc(docRef, dataToSave, { merge: true });
    return id;
  },

  async toggleEnabled(id: string, isEnabled: boolean): Promise<void> {
    const docRef = doc(db, POPUPS_COLLECTION, id);
    await updateDoc(docRef, {
      isEnabled,
      updatedAt: serverTimestamp(),
    });
  },

  async deletePopup(id: string): Promise<void> {
    const docRef = doc(db, POPUPS_COLLECTION, id);
    await deleteDoc(docRef);
  },
};
